import React, { useEffect, useState, useCallback } from "react";
import { FileText, Plus, Trash2, Users, Video, Link as LinkIcon, FileUp, Download, CheckCircle2 } from "lucide-react";
import { Card, Button, Badge, Spinner, PageHeader, Input, Select, Modal } from "../../lib/ui";
import { useToast } from "../../lib/toast";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";

const TYPE_ICON = { PDF: FileText, VIDEO: Video, LINK: LinkIcon };

export default function HRSop() {
  const toast = useToast();
  const { user } = useAuth();
  const canManage = ["SUPER_ADMIN", "OWNER", "HR_MANAGER"].includes(user?.role);
  const [rows, setRows] = useState([]);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [compliance, setCompliance] = useState(null);

  useEffect(() => { api.get("/hr/config").then(({ data }) => setPositions(data.positions)).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/hr/sop"); setRows(data.rows); }
    catch { toast.error("Gagal memuat SOP"); } finally { setLoading(false); }
  }, [toast]);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm("Hapus SOP ini?")) return;
    try { await api.delete(`/hr/sop/${id}`); toast.success("SOP dihapus"); load(); }
    catch { toast.error("Gagal menghapus"); }
  };

  const openCompliance = async (sop) => {
    setCompliance("loading");
    try { const { data } = await api.get(`/hr/sop/${sop.id}/compliance`); setCompliance(data); }
    catch { toast.error("Gagal"); setCompliance(null); }
  };

  return (
    <div data-testid="hr-sop-page">
      <PageHeader title="SOP & Peraturan" subtitle="Unggah SOP (PDF/Video/Tautan), tandai wajib & pantau kepatuhan" icon={FileText}>
        {canManage && <Button onClick={() => setShowUpload(true)} data-testid="hr-sop-upload-btn"><Plus size={15} /> Unggah SOP</Button>}
      </PageHeader>

      {loading ? <div className="flex h-64 items-center justify-center"><Spinner className="h-8 w-8" /></div> : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="hr-sop-list">
          {rows.length === 0 ? <p className="py-6 text-center text-sm text-ink-mute">Belum ada SOP</p> : rows.map((s) => {
            const Icon = TYPE_ICON[s.type] || FileText;
            return (
              <Card key={s.id} data-testid={`hr-sop-${s.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <div className="rounded-lg bg-primary/10 p-2 text-primary"><Icon size={18} /></div>
                    <div><p className="font-semibold text-ink">{s.title}</p><p className="text-xs text-ink-mute">{s.category} · {s.type}</p></div>
                  </div>
                  {s.mandatory && <Badge tone="danger">Wajib</Badge>}
                </div>
                {s.positions?.length > 0 && <p className="mt-2 text-xs text-ink-mute">Posisi: {s.positions.join(", ")}</p>}
                <div className="mt-3 flex items-center justify-between border-t border-line pt-2">
                  <button onClick={() => openCompliance(s)} className="flex items-center gap-1 text-xs font-semibold text-primary" data-testid={`hr-sop-compliance-${s.id}`}>
                    <Users size={13} /> {s.acknowledged_count}/{s.employee_total} acknowledged
                  </button>
                  {canManage && <button onClick={() => del(s.id)} className="rounded-lg p-1.5 text-danger-text hover:bg-red-50" data-testid={`hr-sop-del-${s.id}`}><Trash2 size={15} /></button>}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <UploadModal open={showUpload} onClose={() => setShowUpload(false)} onDone={() => { setShowUpload(false); load(); }} positions={positions} toast={toast} />

      <Modal open={!!compliance} onClose={() => setCompliance(null)} title="Kepatuhan SOP" size="lg">
        {compliance === "loading" ? <div className="flex h-40 items-center justify-center"><Spinner className="h-7 w-7" /></div> : compliance && (
          <div data-testid="hr-sop-compliance-modal">
            <p className="mb-3 font-semibold text-ink">{compliance.title}</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-line text-left text-xs uppercase text-ink-mute">
                  <tr><th className="py-2 pr-3">Karyawan</th><th className="py-2 pr-3">Posisi</th><th className="py-2 pr-3">Cabang</th><th className="py-2 pr-3 text-center">Status</th><th className="py-2 pr-3">Tanggal</th></tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {compliance.rows.map((r) => (
                    <tr key={r.employee_id}>
                      <td className="py-2.5 pr-3 font-medium text-ink">{r.employee_name}</td>
                      <td className="py-2.5 pr-3 text-ink-soft">{r.position}</td>
                      <td className="py-2.5 pr-3 text-ink-soft">{r.branch_name || "—"}</td>
                      <td className="py-2.5 pr-3 text-center">{r.viewed ? <Badge tone="success">Selesai</Badge> : <Badge tone="mute">Belum</Badge>}</td>
                      <td className="py-2.5 pr-3 text-ink-soft">{r.completed_date ? r.completed_date.slice(0, 10) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

function UploadModal({ open, onClose, onDone, positions, toast }) {
  const [form, setForm] = useState({ title: "", category: "General", type: "PDF", url: "", mandatory: false, positions: [] });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const togglePos = (p) => setForm((f) => ({ ...f, positions: f.positions.includes(p) ? f.positions.filter((x) => x !== p) : [...f.positions, p] }));

  const submit = async () => {
    if (!form.title) return toast.error("Judul wajib diisi");
    if (form.type === "PDF" && !file) return toast.error("Unggah file PDF");
    if (form.type !== "PDF" && !form.url) return toast.error("Masukkan URL");
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("title", form.title);
      fd.append("category", form.category);
      fd.append("type", form.type);
      fd.append("mandatory", form.mandatory ? "true" : "false");
      if (form.positions.length) fd.append("positions", form.positions.join(","));
      if (form.type === "PDF" && file) fd.append("file", file);
      if (form.type !== "PDF") fd.append("url", form.url);
      await api.post("/hr/sop", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("SOP diunggah");
      setForm({ title: "", category: "General", type: "PDF", url: "", mandatory: false, positions: [] }); setFile(null);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal mengunggah"); } finally { setSaving(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title="Unggah SOP / Peraturan" size="md"
      footer={<><Button variant="outline" onClick={onClose}>Batal</Button><Button onClick={submit} loading={saving} data-testid="hr-sop-submit"><FileUp size={15} /> Unggah</Button></>}>
      <div className="space-y-3" data-testid="hr-sop-form">
        <Input label="Judul" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="hr-sop-title" />
        <div className="grid grid-cols-2 gap-3">
          <Input label="Kategori" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="hr-sop-category" />
          <Select label="Jenis" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="hr-sop-type">
            <option value="PDF">PDF (Unggah)</option>
            <option value="VIDEO">Video (URL)</option>
            <option value="LINK">Tautan (URL)</option>
          </Select>
        </div>
        {form.type === "PDF"
          ? <div><label className="cf-label">File PDF</label><input type="file" accept="application/pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} className="cf-input" data-testid="hr-sop-file" /></div>
          : <Input label="URL" placeholder="https://..." value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} data-testid="hr-sop-url" />}
        <div>
          <label className="cf-label">Wajib untuk Posisi (kosong = semua)</label>
          <div className="flex flex-wrap gap-1.5">
            {(positions || []).map((p) => (
              <button key={p} type="button" onClick={() => togglePos(p)} data-testid={`hr-sop-pos-${p}`}
                className={`rounded-full px-2.5 py-1 text-xs font-semibold ${form.positions.includes(p) ? "bg-primary text-white" : "bg-stone-100 text-ink-soft"}`}>{p}</button>
            ))}
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm text-ink-soft">
          <input type="checkbox" checked={form.mandatory} onChange={(e) => setForm({ ...form, mandatory: e.target.checked })} data-testid="hr-sop-mandatory" />
          Tandai sebagai wajib
        </label>
      </div>
    </Modal>
  );
}
